/**
 * Soft "Referred By" welcome banner.
 *
 * Surfaces when /investors is loaded with a ?ref=<magic_token> query
 * param — meaning the visitor arrived via a forwarded one-pager PDF.
 * Calls the privacy-safe info endpoint, then shows a subtle social-proof
 * banner naming the original requester. Strengthens conversion from
 * "just curious" → "drops their details in the form" by validating that
 * a peer already vouched for the opportunity.
 */
import React, { useEffect, useState } from 'react';
import { Sparkles, X } from 'lucide-react';

export const ReferredByBanner = () => {
  const [referrer, setReferrer] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get('ref');
    if (!token) return;
    let cancelled = false;
    const fetchInfo = async () => {
      try {
        const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
        const res = await fetch(`${backendUrl}/api/investors/m/${encodeURIComponent(token)}/info`);
        if (!res.ok) return;
        const data = await res.json();
        if (!cancelled && (data?.name || data?.company)) {
          setReferrer(data);
        }
      } catch {
        /* silent — banner is purely a social-proof enhancement */
      }
    };
    fetchInfo();
    return () => { cancelled = true; };
  }, []);

  if (!referrer || dismissed) return null;

  return (
    <div
      data-testid="referred-by-banner"
      role="status"
      style={{
        margin: '0 auto 18px',
        maxWidth: 720,
        background: 'linear-gradient(135deg, #F8FAFC 0%, #FFE3D6 100%)',
        border: '1px solid rgba(10, 37, 64, 0.45)',
        borderLeft: '4px solid #0A2540',
        borderRadius: 14,
        padding: '12px 16px 12px 18px',
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        boxShadow: '0 4px 12px rgba(10, 37, 64, 0.18)',
      }}
    >
      <div
        style={{
          width: 32,
          height: 32,
          borderRadius: '50%',
          background: '#0A2540',
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          boxShadow: '0 2px 6px rgba(10, 37, 64, 0.35)',
        }}
      >
        <Sparkles size={15} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontSize: 10,
            fontWeight: 800,
            letterSpacing: '0.14em',
            textTransform: 'uppercase',
            color: '#9A4A33',
            marginBottom: 2,
          }}
        >
          Welcome — referred via forwarded PDF
        </div>
        <div style={{ fontSize: 13, color: '#0B1528', lineHeight: 1.45 }}>
          {referrer.name && referrer.company ? (
            <>
              You arrived via the one-pager shared by{' '}
              <strong style={{ color: '#1F1A28' }}>{referrer.name}</strong>
              {' '}at{' '}
              <strong style={{ color: '#1F1A28' }}>{referrer.company}</strong>.
              {' '}Good company to be in.
            </>
          ) : referrer.company ? (
            <>
              You arrived via the one-pager shared by{' '}
              <strong style={{ color: '#1F1A28' }}>{referrer.company}</strong>.
              {' '}Good company to be in.
            </>
          ) : (
            <>You arrived via a forwarded HiBFF one-pager — welcome.</>
          )}
          {/* Social proof: how many distinct people have opened this same forwarded PDF. */}
          {(() => {
            const others = Math.max(0, (Number(referrer.unique_referrals) || 0) - 1);
            if (others <= 0) return null;
            return (
              <>
                {' '}<span style={{ color: '#9A4A33' }}>
                  {referrer.name ? `${referrer.name.split(' ')[0]}'s` : 'This'} one-pager has been opened by{' '}
                  <strong>{others}</strong>{' '}
                  other {others === 1 ? 'person' : 'people'} too.
                </span>
              </>
            );
          })()}
        </div>
      </div>
      <button
        type="button"
        onClick={() => setDismissed(true)}
        aria-label="Dismiss referral banner"
        data-testid="referred-by-banner-dismiss"
        style={{
          background: 'transparent',
          border: 'none',
          color: '#9A4A33',
          cursor: 'pointer',
          padding: 4,
          display: 'flex',
          alignItems: 'center',
          flexShrink: 0,
          opacity: 0.6,
        }}
        onMouseEnter={(e) => { e.currentTarget.style.opacity = 1; }}
        onMouseLeave={(e) => { e.currentTarget.style.opacity = 0.6; }}
      >
        <X size={16} />
      </button>
    </div>
  );
};

export default ReferredByBanner;
